import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Icon } from './ui/primitives/Icon';
import { Avatar } from './ui/primitives/Avatar';
import { API_URL } from '../config/api';
import { relativeTime } from '../utils/relativeTime';

interface SearchResult {
  type: 'customer' | 'company' | 'deal' | 'quote' | 'file' | 'event' | 'activity';
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  metadata?: any;
  score?: number;
  // Ranking context from the server (see server/routes/search.ts).
  matchTier?: number;
  lastActivityAt?: string | null;
  activityLabel?: string | null;
  isMine?: boolean;
  owner?: { id: string; name: string; profileImage: string | null } | null;
}

interface SearchResponse {
  results: SearchResult[];
  summary: {
    total: number;
    customers: number;
    companies: number;
    deals: number;
    quotes: number;
    files: number;
    events: number;
    activities: number;
  };
  query: string;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_META: Record<SearchResult['type'], { icon: string; label: string; bg: string; text: string }> = {
  customer: { icon: 'person',       label: 'איש קשר',    bg: 'bg-primary/10',  text: 'text-primary' },
  company:  { icon: 'apartment',    label: 'חברה',        bg: 'bg-sky-100',     text: 'text-sky-700' },
  deal:     { icon: 'work',          label: 'עסקה',        bg: 'bg-emerald-100', text: 'text-emerald-700' },
  quote:    { icon: 'description',  label: 'הצעת מחיר',  bg: 'bg-amber-100',   text: 'text-amber-700' },
  file:     { icon: 'attach_file',   label: 'קובץ',         bg: 'bg-slate-100',   text: 'text-slate-700' },
  event:    { icon: 'event',         label: 'פגישה',        bg: 'bg-purple-100',  text: 'text-purple-700' },
  activity: { icon: 'task_alt',      label: 'פעילות',       bg: 'bg-pink-100',    text: 'text-pink-700' },
};

const FILTER_TABS: { key: keyof SearchResponse['summary']; type: SearchResult['type']; label: string }[] = [
  { key: 'customers',  type: 'customer', label: 'אנשי קשר' },
  { key: 'companies',  type: 'company',  label: 'חברות' },
  { key: 'deals',      type: 'deal',     label: 'עסקאות' },
  { key: 'quotes',     type: 'quote',    label: 'הצעות מחיר' },
  { key: 'events',     type: 'event',    label: 'פגישות' },
  { key: 'activities', type: 'activity', label: 'פעילויות' },
  { key: 'files',      type: 'file',     label: 'קבצים' },
];

// What the latest activity was — the server sends the signal key.
const SIGNAL_LABEL: Record<string, string> = {
  call: 'שיחה', email: 'מייל', whatsapp: 'וואטסאפ', note: 'הערה',
  quote_sent: 'הצעה נשלחה', quote_signed: 'הצעה נחתמה', quote_updated: 'הצעה עודכנה',
  deal_updated: 'פרויקט עודכן', deal_won: 'פרויקט נסגר', viewed: 'הלקוח צפה', updated: 'עודכן',
};

/** Bold the first query word inside the title. */
const highlight = (text: string, q: string): React.ReactNode => {
  const t = q.trim().split(/\s+/)[0];
  if (!t || t.length < 2) return text;
  const i = text.toLowerCase().indexOf(t.toLowerCase());
  if (i < 0) return text;
  return <>{text.slice(0, i)}<mark className="bg-amber-100 text-inherit rounded px-0.5">{text.slice(i, i + t.length)}</mark>{text.slice(i + t.length)}</>;
};

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [summary, setSummary] = useState<SearchResponse['summary'] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [activeFilter, setActiveFilter] = useState<SearchResult['type'] | null>(null);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Only the latest request may set results: typing fast sends several, and
  // an older, slower one used to land last and show the wrong list.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      // Delay focus to let animation settle
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    } else {
      // Reset on close
      setQuery('');
      setResults([]);
      setSummary(null);
      setSelectedIndex(-1);
      setActiveFilter(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (query.trim().length >= 2) {
        performSearch();
      } else {
        setResults([]);
        setSummary(null);
        setActiveFilter(null);
      }
    }, 250);
    return () => clearTimeout(delayedSearch);
    // A chip re-queries the server for that type (more than 5, real order).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, activeFilter]);

  const performSearch = async () => {
    if (!query.trim()) return;
    const reqId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/search`, {
        params: { q: query, ...(activeFilter ? { type: activeFilter, limit: 50 } : {}) },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (reqId !== requestIdRef.current) return; // a newer search is in flight
      setResults(response.data.results);
      // A chip query only counts its own type; keep the "all" totals so the
      // other chips keep their numbers.
      if (!activeFilter) setSummary(response.data.summary);
      setSelectedIndex(0);
    } catch {
      if (reqId !== requestIdRef.current) return;
      setResults([]);
      if (!activeFilter) setSummary(null);
    } finally {
      if (reqId === requestIdRef.current) setIsLoading(false);
    }
  };

  // "הכל": results grouped by type, the group holding the best result first
  // (the server already ranked them). A chip: the server returned that type only.
  const groups = activeFilter
    ? [{ tab: FILTER_TABS.find(t => t.type === activeFilter)!, rows: results }]
    : FILTER_TABS
        .map(tab => ({ tab, rows: results.filter(r => r.type === tab.type) }))
        .filter(g => g.rows.length > 0)
        .sort((a, b) => results.indexOf(a.rows[0]) - results.indexOf(b.rows[0]));
  // Keyboard navigation walks one flat list across the groups.
  const filteredResults = groups.flatMap(g => g.rows);

  // Scroll active item into view
  useEffect(() => {
    if (selectedIndex < 0) return;
    const el = listRef.current?.querySelector(`[data-idx="${selectedIndex}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (!filteredResults.length) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < filteredResults.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : filteredResults.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) handleResultClick(filteredResults[selectedIndex]);
        break;
    }
  };

  // The page a result opens, when it is a page (a file downloads instead).
  // Rendered as a real href so a result can be opened in a new tab.
  const resultPath = (result: SearchResult): string | null => {
    switch (result.type) {
      case 'customer': return `/customers/${result.id}/activity`;
      case 'company':  return `/companies/${result.id}/activity`;
      case 'deal':     return `/deals/${result.id}`;
      // The editor — the public /quote/:shareId page is for the customer.
      case 'quote':    return `/quotes/${result.id}`;
      case 'event':    return '/calendar';
      case 'activity':
        return result.metadata?.customerId ? `/customers/${result.metadata.customerId}/activity` : null;
      default:         return null;
    }
  };

  const handleResultClick = async (result: SearchResult) => {
    const path = resultPath(result) ?? '';
    switch (result.type) {
      case 'file': {
        // Fetch a signed URL through the authorized download route (search
        // no longer presigns — it returned working URLs for every file).
        const m = result.metadata || {};
        const base = m.dealId ? `deals/${m.dealId}`
          : m.customerId ? `customers/${m.customerId}`
          : m.companyId ? `companies/${m.companyId}`
          : null;
        if (base) {
          try {
            const token = localStorage.getItem('token');
            const r = await axios.get(
              `${API_URL}/api/${base}/files/${result.id}/download`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
            if (r.data?.url) window.open(r.data.url, '_blank');
          } catch {
            /* the download route 404s a file that isn't yours */
          }
        }
        onClose();
        return;
      }
    }
    if (path) { navigate(path); onClose(); }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-slate-900/50 backdrop-blur-sm flex items-start justify-center px-4 pt-[10vh] pb-4 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="חיפוש מהיר"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[80vh] flex flex-col bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden animate-scale-in"
      >
        {/* Search header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <Icon name="search" size={22} className="text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="חפש אנשי קשר, חברות, עסקאות, הצעות מחיר, קבצים..."
            value={query}
            // A new query searches everything again: a chip query only counts
            // its own type, so staying on the chip left stale chip counts.
            onChange={(e) => { setQuery(e.target.value); setActiveFilter(null); }}
            onKeyDown={handleKeyDown}
            className="flex-1 bg-transparent border-0 text-base font-semibold text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none"
          />
          {query && (
            <button
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              className="size-7 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="נקה"
            >
              <Icon name="close" size={16} />
            </button>
          )}
          <kbd className="hidden sm:inline-flex text-[10px] font-mono bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5 text-slate-500">
            ESC
          </kbd>
        </div>

        {/* Filters */}
        {summary && summary.total > 0 && (
          <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-1.5 overflow-x-auto">
            <button
              onClick={() => { setActiveFilter(null); setSelectedIndex(0); }}
              className={[
                'inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors',
                !activeFilter ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 hover:bg-slate-200',
              ].join(' ')}
            >
              הכל
              <span className={[
                'text-[10px] font-bold tabular-nums',
                !activeFilter ? 'text-white/80' : 'text-slate-400',
              ].join(' ')}>
                {summary.total}
              </span>
            </button>
            {FILTER_TABS.map(tab => {
              const count = summary[tab.key];
              if (!count) return null;
              const active = activeFilter === tab.type;
              const meta = TYPE_META[tab.type];
              return (
                <button
                  key={tab.type}
                  onClick={() => { setActiveFilter(active ? null : tab.type); setSelectedIndex(0); }}
                  className={[
                    'inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold whitespace-nowrap transition-colors',
                    active ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 hover:bg-slate-200',
                  ].join(' ')}
                >
                  <Icon name={meta.icon} size={12} />
                  {tab.label}
                  <span className={[
                    'text-[10px] font-bold tabular-nums',
                    active ? 'text-white/80' : 'text-slate-400',
                  ].join(' ')}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Body */}
        <div ref={listRef} className="flex-1 overflow-y-auto min-h-[120px]">
          {/* Initial empty state */}
          {!query && (
            <div className="px-5 py-12 text-center">
              <div className="size-16 mx-auto rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center mb-4">
                <Icon name="search" size={28} />
              </div>
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">
                התחל להקליד כדי לחפש
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-6">
                חיפוש בכל המערכת — לקוחות, חברות, עסקאות והצעות מחיר
              </div>
              <div className="flex items-center justify-center gap-3 text-[11px] text-slate-500">
                <span className="flex items-center gap-1.5">
                  <kbd className="font-mono bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5">↑↓</kbd>
                  ניווט
                </span>
                <span className="flex items-center gap-1.5">
                  <kbd className="font-mono bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5">↵</kbd>
                  בחירה
                </span>
                <span className="flex items-center gap-1.5">
                  <kbd className="font-mono bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-0.5">ESC</kbd>
                  סגירה
                </span>
              </div>
            </div>
          )}

          {/* Typing hint */}
          {query.length > 0 && query.length < 2 && (
            <div className="px-5 py-8 text-center text-sm text-slate-500">
              הקלד לפחות 2 תווים לחיפוש
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="px-5 py-12 flex flex-col items-center gap-3 text-slate-500">
              <Icon name="progress_activity" size={24} className="animate-spin text-primary" />
              <span className="text-sm font-semibold">מחפש...</span>
            </div>
          )}

          {/* No results — offer to create what was searched for */}
          {!isLoading && query.length >= 2 && filteredResults.length === 0 && (
            <div className="px-5 py-12 text-center">
              <div className="size-16 mx-auto rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-400 flex items-center justify-center mb-4">
                <Icon name="search_off" size={28} />
              </div>
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">
                לא נמצאו תוצאות
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mb-6">
                נסה במילים אחרות או בדוק את הכתיב
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                <button
                  onClick={() => {
                    navigate(`/customers?tab=customers&create=${encodeURIComponent(query.trim())}`);
                    onClose();
                  }}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-primary text-white text-xs font-semibold hover:bg-primary/90 transition-colors max-w-full"
                >
                  <Icon name="add" size={16} className="flex-shrink-0" />
                  <span className="truncate">הוסף איש קשר בשם «{query.trim()}»</span>
                </button>
                <button
                  onClick={() => {
                    navigate(`/customers?tab=companies&create=${encodeURIComponent(query.trim())}`);
                    onClose();
                  }}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-semibold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors max-w-full"
                >
                  <Icon name="add" size={16} className="flex-shrink-0" />
                  <span className="truncate">הוסף חברה</span>
                </button>
              </div>
            </div>
          )}

          {/* Results — grouped by type in "הכל", one list for a chip */}
          {!isLoading && filteredResults.length > 0 && (
            <div className="py-1">
              {groups.map(({ tab, rows }) => {
                const more = !activeFilter && summary ? (summary[tab.key] || 0) - rows.length : 0;
                return (
                  <div key={tab.type} className="pb-1">
                    {!activeFilter && (
                      <div className="px-5 pt-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                        {tab.label}
                      </div>
                    )}
                    {rows.map((result) => {
                      const index = filteredResults.indexOf(result);
                      const meta = TYPE_META[result.type];
                      const active = index === selectedIndex;
                      const href = resultPath(result);
                      // A page result is a real link: Ctrl/Cmd/middle-click and
                      // the right-click menu open it in a new tab; a plain click
                      // still navigates in place and closes the search.
                      const Row: any = href ? 'a' : 'button';
                      return (
                        <Row
                          key={`${result.type}-${result.id}`}
                          data-idx={index}
                          {...(href ? { href } : { type: 'button' })}
                          onClick={(e: React.MouseEvent) => {
                            if (href && (e.metaKey || e.ctrlKey || e.shiftKey)) return;
                            e.preventDefault();
                            handleResultClick(result);
                          }}
                          onMouseEnter={() => setSelectedIndex(index)}
                          className={[
                            'w-full flex items-center gap-3 px-5 py-2.5 transition-colors text-start',
                            active ? 'bg-primary/5 dark:bg-slate-800' : '',
                          ].join(' ')}
                        >
                          {result.type === 'customer' || result.type === 'company' ? (
                            <Avatar name={result.title} size="sm" />
                          ) : (
                            <div className={`size-8 rounded-lg ${meta.bg} ${meta.text} flex items-center justify-center flex-shrink-0`}>
                              <Icon name={meta.icon} size={16} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm truncate">{highlight(result.title, query)}</span>
                              <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${meta.bg} ${meta.text} flex-shrink-0`}>
                                {meta.label}
                              </span>
                            </div>
                            {/* Why this row is relevant: mine, and the latest activity. */}
                            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5 min-w-0">
                              {result.isMine && (
                                <span className="flex-shrink-0 rounded px-1 py-px bg-primary/10 text-primary font-bold">שלך</span>
                              )}
                              {result.lastActivityAt && result.activityLabel && (
                                <span className="flex-shrink-0 text-emerald-700">
                                  {SIGNAL_LABEL[result.activityLabel] || 'פעילות'} · {relativeTime(result.lastActivityAt)}
                                </span>
                              )}
                              <span className="truncate">
                                {[result.subtitle, result.description].filter(Boolean).join(' · ')}
                              </span>
                            </div>
                          </div>
                          {result.owner && (
                            <span title={result.owner.name} className="flex-shrink-0">
                              <Avatar name={result.owner.name} src={result.owner.profileImage} size="xs" />
                            </span>
                          )}
                          <Icon
                            name="chevron_left"
                            size={16}
                            className={[
                              'flex-shrink-0 transition-opacity',
                              active ? 'text-primary opacity-100' : 'text-slate-300 opacity-0 group-hover:opacity-100',
                            ].join(' ')}
                          />
                        </Row>
                      );
                    })}
                    {more > 0 && (
                      <button
                        type="button"
                        onClick={() => { setActiveFilter(tab.type); setSelectedIndex(0); }}
                        className="px-5 py-1.5 text-xs font-semibold text-primary hover:underline"
                      >
                        הצג עוד ({more})
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {filteredResults.length > 0 && (
          <div className="px-5 py-2.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex items-center justify-between text-[11px] text-slate-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="font-mono bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1 py-0.5">↑↓</kbd>
                ניווט
              </span>
              <span className="flex items-center gap-1">
                <kbd className="font-mono bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1 py-0.5">↵</kbd>
                פתיחה
              </span>
            </div>
            <span className="tabular-nums">{filteredResults.length} תוצאות</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;
